import {
  Button,
  Flex,
  PasswordInput,
  TextInput,
  ValidationText,
} from '@utilitywarehouse/hearth-react';
import { FormikProps, useFormikContext } from 'formik';

import { Dialog, DialogActions, DialogID, useDialog } from '@components/Dialog';
import { useFieldProps } from '@hooks/useFieldProps';

import { PartnerFormValues } from './types';

interface SupportLoginModalProps {
  supportingLoginError: string;
  continueWithoutSupport: () => void;
}

interface SupportLoginModalUIProps {
  dialogId: DialogID;
  supportingLoginError: string;
  onClose: () => void;
  onContinueWithoutSupport: () => void;
}

const SupportLoginModalUI = ({
  dialogId,
  supportingLoginError,
  onClose,
  onContinueWithoutSupport,
}: SupportLoginModalUIProps) => {
  const {
    values,
    errors,
    handleChange,
    handleBlur,
    touched,
    submitCount,
    submitForm,
  }: FormikProps<PartnerFormValues> = useFormikContext();

  const getFieldProps = useFieldProps<PartnerFormValues>({
    values,
    errors,
    handleChange,
    handleBlur,
    touched,
    submitCount,
  });

  return (
    <Dialog
      id={dialogId}
      heading="Supporting Partner login"
      description="Ask your supporting Partner to log in with their UW details below."
      onClose={onClose}
      preventDismiss
    >
      <Flex direction="column" spacing="xl">
        <TextInput
          {...getFieldProps('supportingPartnerEmail')}
          className="partnerAppointment__supportingPartnerLogin"
          label="Email address"
          required
        />
        <PasswordInput
          className="partnerAppointment__supportingPartnerLogin"
          label="Password"
          required
          {...getFieldProps('supportingPartnerPassword')}
          autoComplete="current-password"
        />
        {supportingLoginError && (
          <ValidationText status="invalid">
            {supportingLoginError}
          </ValidationText>
        )}
      </Flex>
      <DialogActions>
        <Button variant="ghost" onClick={onContinueWithoutSupport}>
          Cancel
        </Button>
        <Button variant="solid" onClick={submitForm}>
          Log in and start presentation
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const SupportLoginModal = ({
  supportingLoginError,
  continueWithoutSupport,
}: SupportLoginModalProps) => {
  const dialog = useDialog(DialogID.SUPPORT_LOGIN);

  const handleClose = () => {
    dialog.setOpen(false);
  };

  const handleContinueWithoutSupport = () => {
    dialog.setOpen(false);
    continueWithoutSupport();
  };

  return (
    <SupportLoginModalUI
      dialogId={dialog.id}
      supportingLoginError={supportingLoginError}
      onClose={handleClose}
      onContinueWithoutSupport={handleContinueWithoutSupport}
    />
  );
};

export type { SupportLoginModalProps, SupportLoginModalUIProps };

export { SupportLoginModal, SupportLoginModalUI };
